import { useEffect, useRef, useState, type ReactNode } from 'react';
import jsQR from 'jsqr';
import { Modal } from './Modal.js';
import { isPortablePlayerLink, pairingCodeFromScan } from '../lib/pairing.js';

/** The slice of the Shape Detection API we use (absent from lib.dom and from iOS Safari). */
interface NativeBarcodeDetector {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue: string }>>;
}
type BarcodeDetectorCtor = new (options: { formats: string[] }) => NativeBarcodeDetector;

const SCAN_EVERY_MS = 200;

/**
 * Scans a player's pairing QR (spec §2.3) with the device camera and reports
 * the pairing code. Uses the native `BarcodeDetector` where it exists
 * (Chrome/Android) and falls back to jsQR (iOS Safari, Firefox). The camera
 * is released when the dialog closes. Needs a secure context (HTTPS).
 */
export function QrScanner({ onCode, onClose }: { onCode: (code: string) => void; onClose: () => void }): ReactNode {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string>();
  const [unrelated, setUnrelated] = useState<string>();

  // Keep the latest callback without restarting the camera when it changes.
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;

  useEffect(() => {
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const Detector = (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
    const native = Detector ? new Detector({ formats: ['qr_code'] }) : undefined;

    async function decode(video: HTMLVideoElement): Promise<string | null> {
      if (native) {
        const found = await native.detect(video);
        return found[0]?.rawValue ?? null;
      }
      if (!context || video.videoWidth === 0) return null;
      // Downscale large frames — jsQR is CPU-bound and 640px is plenty for a QR.
      const scale = Math.min(1, 640 / Math.max(video.videoWidth, video.videoHeight));
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const image = context.getImageData(0, 0, canvas.width, canvas.height);
      return jsQR(image.data, image.width, image.height, { inversionAttempts: 'dontInvert' })?.data ?? null;
    }

    async function tick(): Promise<void> {
      const video = videoRef.current;
      if (cancelled || !video) return;
      try {
        const text = video.readyState >= video.HAVE_CURRENT_DATA ? await decode(video) : null;
        if (cancelled) return;
        if (text !== null) {
          const code = pairingCodeFromScan(text);
          if (code) {
            onCodeRef.current(code);
            return; // stop scanning; the parent closes us
          }
          setUnrelated(
            isPortablePlayerLink(text)
              ? 'That’s the player’s portable CMS code. On the player, tap “Use Akarya Cloud”, then scan the QR it shows.'
              : 'That QR isn’t a screen pairing code. Point at the code on the screen.',
          );
        }
      } catch {
        // A frame that fails to decode is normal; keep going.
      }
      timer = setTimeout(() => void tick(), SCAN_EVERY_MS);
    }

    async function start(): Promise<void> {
      if (!navigator.mediaDevices?.getUserMedia) {
        setProblem('This browser can’t use the camera here. Open the console over HTTPS, or type the code instead.');
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      } catch (err) {
        setProblem(
          err instanceof DOMException && err.name === 'NotAllowedError'
            ? 'Camera access was blocked. Allow it in the browser’s site settings, or type the code instead.'
            : 'No camera available. Type the code shown on the screen instead.',
        );
        return;
      }
      if (cancelled) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play().catch(() => undefined);
      void tick();
    }

    void start();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  return (
    <Modal title="Scan screen QR" onClose={onClose}>
      <div className="stack">
        {problem ? (
          <div className="form-error">{problem}</div>
        ) : (
          <>
            <video ref={videoRef} className="qr-scanner-video" playsInline muted autoPlay />
            <span className="muted">
              {unrelated ?? 'Point the camera at the QR code on the screen.'}
            </span>
          </>
        )}
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
